import { gql } from '@urql/core';
import React from 'react';
import { SocialCardData, SocialCard } from './SocialCard';
import { Content_File, Espin_Message_Type_Enum, MessageCardFragment } from '../../../../generated/spin-graphql';
import { useAppState } from '../../../../client/providers/AppProvider';
import { stripHTMLForPreview } from '../../../../shared/shared.utils';

type Props = {
    message: MessageCardFragment;
    viewedAt: any;
};

gql`
    fragment MessageCard on espin_message {
        message_type
        viewed_at
        contents
        parent_user_id
        player_user {
            deleted_at
            player_profile {
                user_id
                first_name
                last_name
                slug
                profile_image {
                    ...SpinContentImageDisplay
                }
            }
        }
        college {
            name
            slug
            logo {
                ...SpinContentImageDisplay
            }
        }
        high_school {
            name
            slug
            profile_pic_file {
                ...SpinContentImageDisplay
            }
        }
        college_user {
            deleted_at
        }
    }
`;

function messageTierCheck(message: MessageCardFragment, subscribed: boolean, subTier: number) {
    if (message?.message_type === Espin_Message_Type_Enum.HighSchoolMessagesCollege) {
        return subscribed && subTier >= 1;
    }
    return subscribed;
}

export const MessageCard: React.FC<Props> = ({ message, viewedAt }) => {
    const { subscribed, subtier } = useAppState();
    const messageType = message?.message_type;
    let fullName: string = '';
    let profileImg: Partial<Content_File>;
    let slug: string = '';
    let deletedUser: boolean = false;

    if (
        messageType === Espin_Message_Type_Enum.CollegeMessagesPlayer ||
        messageType === Espin_Message_Type_Enum.CollegeMessagesHighSchool
    ) {
        // if college is null, then it's likely been deleted or unverified somehow....
        // i think in terms of data integrity, it's easiest to default this somehow.
        if (!!!message?.college) {
            fullName = 'Unverified College';
            slug = `dashboard/messages`;
            profileImg = null;
        } else {
            fullName = message?.college.name;
            profileImg = subscribed ? message?.college.logo : null;
            slug = `dashboard/messages/?college_slug=${message?.college.slug}`;
            if (!!message?.college_user?.deleted_at) {
                deletedUser = true;
            }
        }
    } else if (messageType === Espin_Message_Type_Enum.PlayerMessagesCollege) {
        fullName = `${message?.player_user?.player_profile?.first_name} ${message?.player_user?.player_profile?.last_name}`;
        profileImg = subscribed ? message?.player_user?.player_profile?.profile_image : null;
        slug = `dashboard/messages/?player_slug=${message?.player_user?.player_profile.slug}`;
        if (!!message?.player_user?.deleted_at) {
            deletedUser = true;
        }
    } else if (messageType === Espin_Message_Type_Enum.HighSchoolMessagesCollege) {
        fullName = message?.high_school?.name ?? 'A High School';
        profileImg = subscribed ? message?.high_school.profile_pic_file : null;
        slug = `dashboard/messages/?high_school_slug=${message?.high_school?.slug}`;
        if (!!message?.college_user?.deleted_at) {
            deletedUser = true;
        }
    } else if (
        messageType === Espin_Message_Type_Enum.AdminMessagesCollege ||
        messageType === Espin_Message_Type_Enum.AdminMessagesPlayer
    ) {
        fullName = 'Stay Plugged In';
        profileImg = null;
        slug = `dashboard/messages`;
    }

    const data: SocialCardData = {
        label: 'New Message!',
        subLabel: `${fullName}${
            !!message?.parent_user_id && messageType === Espin_Message_Type_Enum.PlayerMessagesCollege
                ? "'s Parent"
                : ''
        }: ${
            messageTierCheck(message, subscribed, subtier)
                ? stripHTMLForPreview(message?.contents)
                : 'Upgrade to Premium to see this message!'
        }`,
        picAltText: `${fullName}'s picture`,
        profileImg,
        slug,
        unread: !viewedAt,
        deletedUser,
        subTierRequired: 1,
    };

    return (
        <>
            <SocialCard data={data} />
        </>
    );
};
